import { CreateShippingRateVariables } from "@saleor/shipping/types/CreateShippingRate";
import { ShippingZone_shippingZone_shippingMethods } from "@saleor/shipping/types/ShippingZone";
import { UpdateShippingRateVariables } from "@saleor/shipping/types/UpdateShippingRate";
import { ShippingZoneUrlQueryParams } from "@saleor/shipping/urls";
import {
  DeliveryTypeEnum,
  DistancePricesInput,
  ShippingMethodStatusEnum,
  ShippingMethodTypeEnum
} from "@saleor/types/globalTypes";

import { FormData as ShippingZoneRateDialogFormData } from "../../components/ShippingZoneRateDialog";

function getValue(
  value: string,
  hasLimits: boolean,
  forceZeroOnLimits: boolean = false
): string | null {
  if (hasLimits && forceZeroOnLimits) {
    return "0.00";
  } else if (!value || hasLimits) {
    return null;
  } else {
    return parseFloat(value).toFixed(2);
  }
}

export function getCreateShippingRateVariables(
  data: ShippingZoneRateDialogFormData,
  params: ShippingZoneUrlQueryParams,
  id: string
): CreateShippingRateVariables {
  return {
    input: {
      deliveryType: data.deliveryType,
      maximumOrderPrice:
        params.type === ShippingMethodTypeEnum.PRICE
          ? getValue(data.maxValue, data.noLimits)
          : null,
      maximumOrderWeight:
        params.type === ShippingMethodTypeEnum.WEIGHT
          ? getValue(data.maxValue, data.noLimits)
          : null,

      minimumOrderPrice:
        params.type === ShippingMethodTypeEnum.PRICE
          ? getValue(data.minValue, data.noLimits, true)
          : null,
      minimumOrderWeight:
        params.type === ShippingMethodTypeEnum.WEIGHT
          ? getValue(data.minValue, data.noLimits)
          : null,
      name: data.name,
      price: data.isFree ? "0.00" : parseFloat(data.price).toFixed(2),
      shippingZone: id,
      type: params.type
    }
  };
}

export function getUpdateShippingRateVariables(
  data: ShippingZoneRateDialogFormData,
  shippingRate: ShippingZone_shippingZone_shippingMethods,
  shippingZoneId: string
): UpdateShippingRateVariables {
  const base: UpdateShippingRateVariables = {
    id: shippingRate.id,
    input: {
      deliveryType: data.deliveryType,
      name: data.name,
      price: data.isFree ? "0.00" : parseFloat(data.price).toFixed(2),
      shippingZone: shippingZoneId,
      type: shippingRate.type
    }
  };

  if (shippingRate.type === ShippingMethodTypeEnum.PRICE) {
    return {
      ...base,
      input: {
        ...base.input,
        maximumOrderPrice: getValue(data.maxValue, data.noLimits),
        minimumOrderPrice: getValue(data.minValue, data.noLimits, true)
      }
    };
  } else if (shippingRate.type === ShippingMethodTypeEnum.LOCATION) {
    return {
      ...base,
      input: {
        deliveryType: data.deliveryType,
        maximumOrderPrice: 0,
        minimumOrderPrice: 0,
        status: data.status
      }
    };
  }

  return {
    ...base,
    input: {
      ...base.input,
      maximumOrderWeight: getValue(data.maxValue, data.noLimits),
      minimumOrderWeight: getValue(data.minValue, data.noLimits)
    }
  };
}

export function getCreateShippingLocationRateVariables(
  deliveryType: DeliveryTypeEnum,
  distancePrices: DistancePricesInput[],
  name: string,
  status: ShippingMethodStatusEnum,
  id: string
): CreateShippingRateVariables {
  return {
    input: {
      deliveryType,
      distancePrices: distancePrices.map(item => ({
        fromKm: item.fromKm.toFixed(2),
        price: item.price.toFixed(2),
        toKm: item.toKm.toFixed(2)
      })),
      maximumOrderPrice: 0,
      maximumOrderWeight: 0,
      minimumOrderPrice: 0,
      minimumOrderWeight: 0,
      name,
      price: 0,
      shippingZone: id,
      status,
      type: ShippingMethodTypeEnum.LOCATION
    }
  };
}

export function getUpdateShippingLocationRateVariables(
  deliveryType: DeliveryTypeEnum,
  distancePrices: DistancePricesInput[],
  name: string,
  status: ShippingMethodStatusEnum,
  id: string
): UpdateShippingRateVariables {
  return {
    id,
    input: {
      deliveryType,
      distancePrices: distancePrices.map(item => ({
        fromKm: item.fromKm.toFixed(2),
        price: item.price.toFixed(2),
        toKm: item.toKm.toFixed(2)
      })),
      name,
      status,
      type: ShippingMethodTypeEnum.LOCATION
    }
  };
}

export function getUpdateStatusShippingRateVariables(
  deliveryType: DeliveryTypeEnum.EXPRESS,
  status: ShippingMethodStatusEnum,
  id: string
): UpdateShippingRateVariables {
  return {
    id,
    input: {
      deliveryType,
      status:
        status === ShippingMethodStatusEnum.ACT
          ? ShippingMethodStatusEnum.INACTIVE
          : ShippingMethodStatusEnum.ACT
    }
  };
}
